import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Search, Clock, ArrowLeft, FileText, Users, Activity, Calendar, BarChart3, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import auditService from '../services/auditService';
import toast from 'react-hot-toast';

// Dicionário de tradução de Recursos para Português
const RESOURCES = {
  // Autenticação
  'auth': 'Autenticação',
  'refresh_tokens': 'Tokens de Sessão',

  // Cadastros
  'users': 'Usuários',
  'clients': 'Clientes',
  'client': 'Clientes',
  'client_first_contacts': 'Primeiros Contatos',
  'first_contact': 'Primeiros Contatos',
  'client_surveys': 'Levantamentos',
  'suppliers': 'Fornecedores',

  // Operações
  'appointments': 'Agendamentos',
  'projects': 'Projetos',
  'budget_sheets': 'Planilhas de Orçamento',
  'pre_projects': 'Pré-Projetos',
  'contracts': 'Contratos',
  'appointment_sessions': 'Sessões de Agendamento',

  // Inventário
  'inventory_products': 'Produtos',
  'inventory_categories': 'Categorias',
  'inventory_brands': 'Marcas',
  'inventory_units': 'Unidades',
  'inventory_warehouses': 'Armazéns',
  'inventory_stock': 'Estoque',
  'product_variants': 'Variantes de Produto',

  // Financeiro
  'finance_incomes': 'Receitas',
  'finance_expenses': 'Despesas',
  'expenses': 'Despesas',
  'expected_incomes': 'Receitas Previstas',
  'expense_categories': 'Categorias de Despesa',
  'expense_types': 'Tipos de Despesa',
  'payment_conditions': 'Condições de Pagamento',
  'company_cashbox': 'Caixa da Empresa',
  'cashbox_categories': 'Categorias do Caixa',
  'cashbox_transactions': 'Transações do Caixa',
  'profit_sharing': 'Participação nos Lucros',
  'tax_contributions': 'Contribuições Fiscais',
  'fiscal_documents': 'Documentos Fiscais',

  // RH
  'payroll': 'Folha de Pagamento',
  'payslips': 'Holerites',
  'advances': 'Adiantamentos',
  'extra_advances': 'Adiantamentos Extras',
  'overtime_records': 'Horas Extras',
  'worked_saturdays': 'Sábados Trabalhados',
  'absences': 'Faltas',
  'vacations': 'Férias',
  'work_schedules': 'Jornadas de Trabalho',
  'time_clock': 'Ponto',
  'time_clock_records': 'Registros de Ponto',
  'user_documents': 'Documentos de Usuários',
  'user_salary_configs': 'Configurações de Salário',

  // Configurações
  'settings': 'Configurações',
  'permissions': 'Permissões',
  'presentation_settings': 'Configurações de Apresentação',
  'settings_client_access': 'Acesso de Clientes',

  // Outros
  'purchase_orders': 'Pedidos de Compra',
  'deliveries': 'Entregas',
  'production_contacts': 'Contatos de Produção',
  'sketch_projects': 'Projetos Croqui',
  'notifications': 'Notificações'
};

// Dicionário de tradução de Ações para Português
const ACTIONS = {
  // Autenticação
  'LOGIN_ATTEMPT': 'Tentativa de Login',
  'LOGIN_SUCCESS': 'Login Realizado',
  'LOGIN_FAILED': 'Login Falhou',
  'LOGOUT': 'Logout',
  'TOKEN_REFRESH': 'Token Atualizado',

  // CRUD
  'CREATE': 'Criado',
  'CREATE_USER': 'Usuário Criado',
  'UPDATE': 'Atualizado',
  'UPDATE_USER': 'Usuário Atualizado',
  'DELETE': 'Deletado',
  'DELETE_MASS': 'Exclusão em Massa',
  'PERMANENTLY_DELETE': 'Deletado Permanentemente',

  // Especiais
  'UPLOAD': 'Arquivo Enviado',
  'DOWNLOAD': 'Arquivo Baixado',
  'APPROVE': 'Aprovado',
  'REJECT': 'Rejeitado',
  'STATUS_CHANGE': 'Status Alterado',
  'APPROVE_3D': 'Projeto 3D Aprovado',
  'CHANGE_PASSWORD': 'Senha Alterada',
  'ADMIN_RESET_PASSWORD': 'Senha Resetada pelo Admin',
  'CLOCK_IN': 'Entrada Registrada',
  'CLOCK_OUT': 'Saída Registrada',

  // Financeiro
  'PAID': 'Pago',
  'RECEIVED': 'Recebido',
  'CANCELLED': 'Cancelado',

  // Padrão fallback
  'DEFAULT': 'Operação'
};

// Função auxiliar para traduzir recursos
const translateResource = (resource) => {
  return RESOURCES[resource] || resource;
};

// Função auxiliar para traduzir ações
const translateAction = (action) => {
  return ACTIONS[action] || action;
};

const AuditLogs = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [groupedActions, setGroupedActions] = useState({});
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({
    action: '',
    resource: '',
    user_id: '',
    date_from: '',
    date_to: '',
    search: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 0
  });
  const [activeTab, setActiveTab] = useState('logs'); // logs | stats

  useEffect(() => {
    if (user.role !== 'GERENCIA') {
      navigate('/');
      return;
    }
    loadLogs(1);
    loadActions();
    loadStats();
  }, []);

  const loadLogs = async (page = pagination.page) => {
    try {
      setLoading(true);
      const response = await auditService.getAuditLogs({ page, limit: pagination.limit, ...filters });
      setLogs(response.data || []);

      if (response.pagination) {
        setPagination(prev => ({
          ...prev,
          page: response.pagination.page,
          total: response.pagination.total,
          pages: response.pagination.pages
        }));
      }
    } catch (error) {
      console.error('Erro ao carregar logs:', error);
      toast.error('Erro ao carregar logs');
    } finally {
      setLoading(false);
    }
  };

  const loadActions = async () => {
    try {
      const response = await auditService.getAuditActions();
      const grouped = {};
      response.data.forEach(item => {
        if (!grouped[item.resource]) {
          grouped[item.resource] = {
            resource: item.resource,
            label: item.label,
            actions: []
          };
        }
        grouped[item.resource].actions.push(item);
      });
      setGroupedActions(grouped);
      setUsers(response.users || []);
    } catch (error) {
      console.error('Erro ao carregar ações:', error);
    }
  };

  const loadStats = async () => {
    try {
      const response = await auditService.getAuditStats();
      setStats(response.data);
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const applyFilters = () => {
    loadLogs(1);
  };

  const clearFilters = () => {
    setFilters({
      action: '',
      resource: '',
      user_id: '',
      date_from: '',
      date_to: '',
      search: ''
    });
    loadLogs(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.pages) {
      loadLogs(newPage);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR');
  };

  // Recursos únicos do filtro
  const resources = Object.keys(groupedActions);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/levantamentos')}
              className="p-2 hover:bg-gray-100 rounded-lg"
              title="Voltar"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                📋 Centro de Auditoria
              </h1>
              <p className="text-gray-600">
                Histórico completo de operações do sistema
              </p>
            </div>
          </div>

          {/* Abas */}
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab('logs')}
              className={`px-4 py-2 rounded-lg ${
                activeTab === 'logs' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'
              }`}
            >
              <Activity className="w-4 h-4 inline mr-2" />
              Logs
            </button>
            <button
              onClick={() => { setActiveTab('stats'); loadStats(); }}
              className={`px-4 py-2 rounded-lg ${
                activeTab === 'stats' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'
              }`}
            >
              <BarChart3 className="w-4 h-4 inline mr-2" />
              Estatísticas
            </button>
          </div>
        </div>

        {/* Filtros */}
        {activeTab === 'logs' && (
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Recurso</label>
              <select
                value={filters.resource}
                onChange={(e) => handleFilterChange('resource', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value="">Todos os recursos</option>
                {resources.map(r => (
                  <option key={r} value={r}>{translateResource(r)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ação</label>
              <select
                value={filters.action}
                onChange={(e) => handleFilterChange('action', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value="">Todas as ações</option>
                {filters.resource && groupedActions[filters.resource]?.actions.map(a => (
                  <option key={a.action} value={a.action}>{translateAction(a.action)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Usuário</label>
              <select
                value={filters.user_id}
                onChange={(e) => handleFilterChange('user_id', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value="">Todos os usuários</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>{u.full_name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Data Inicial</label>
              <input
                type="date"
                value={filters.date_from}
                onChange={(e) => handleFilterChange('date_from', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Data Final</label>
              <input
                type="date"
                value={filters.date_to}
                onChange={(e) => handleFilterChange('date_to', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Buscar</label>
              <input
                type="text"
                placeholder="Buscar em detalhes..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>
          </div>
        )}

        {activeTab === 'logs' && (
          <div className="mt-4 flex gap-2">
            <button
              onClick={applyFilters}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <Search className="w-4 h-4" />
              Filtrar
            </button>
            <button
              onClick={clearFilters}
              className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 flex items-center gap-2"
            >
              <X className="w-4 h-4" />
              Limpar
            </button>
          </div>
        )}
      </div>

      {/* Conteúdo */}
      {activeTab === 'logs' ? (
        <>
          {/* Tabela de Logs */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                Registros de Auditoria ({pagination.total} total)
              </h2>
            </div>

            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-gray-500 mt-2">Carregando logs...</p>
              </div>
            ) : logs.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p className="text-lg mb-2">Nenhum log encontrado</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Data/Hora
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Usuário
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Recurso
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Ação
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Detalhes
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        IP
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {logs.map((log) => (
                      <tr key={log.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-gray-400" />
                            {formatDate(log.created_at)}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-gray-900">
                            {log.user_name || 'Sistema'}
                          </div>
                          {log.user_email && (
                            <div className="text-xs text-gray-500">{log.user_email}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span className="bg-gray-100 px-2 py-1 rounded text-gray-700">
                            {translateResource(log.resource)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            log.action?.includes('DELETE') || log.action?.includes('LOGIN_FAILED') ? 'bg-red-100 text-red-800' :
                            log.action?.includes('CREATE') || log.action?.includes('LOGIN') ? 'bg-green-100 text-green-800' :
                            log.action?.includes('UPDATE') || log.action?.includes('APPROVE') ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {translateAction(log.action)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 max-w-xs">
                          {log.details_parsed && (
                            <details className="cursor-pointer">
                              <summary className="truncate">
                                {typeof log.details_parsed === 'object'
                                  ? JSON.stringify(log.details_parsed).substring(0, 50) + '...'
                                  : String(log.details_parsed).substring(0, 50)}
                              </summary>
                              <pre className="text-xs mt-2 p-2 bg-gray-100 rounded overflow-x-auto">
                                {JSON.stringify(log.details_parsed, null, 2)}
                              </pre>
                            </details>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {log.ip_address || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Paginação */}
          {!loading && logs.length > 0 && (
            <div className="bg-white rounded-lg shadow px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Página {pagination.page} de {pagination.pages}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page <= 1}
                    className="p-2 rounded hover:bg-gray-100 disabled:opacity-50"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page >= pagination.pages}
                    className="p-2 rounded hover:bg-gray-100 disabled:opacity-50"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        /* Estatísticas */
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Ações por dia */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">
              <Calendar className="w-5 h-5 inline mr-2" />
              Atividade por Dia (Últimos 30 dias)
            </h3>
            {stats?.daily && stats.daily.length > 0 ? (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {stats.daily.map((d, i) => (
                  <div key={i} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                    <span className="text-sm">{new Date(d.date).toLocaleDateString('pt-BR')}</span>
                    <span className="font-medium">{d.total} ações</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">Sem dados</p>
            )}
          </div>

          {/* Ações mais comuns */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">
              <Activity className="w-5 h-5 inline mr-2" />
              Ações mais comuns
            </h3>
            {stats?.by_action && stats.by_action.length > 0 ? (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {stats.by_action.map((a, i) => (
                  <div key={i} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                    <span className="text-sm">{translateAction(a.action)}</span>
                    <span className="font-medium">{a.total}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">Sem dados</p>
            )}
          </div>

          {/* Recursos mais alterados */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">
              <FileText className="w-5 h-5 inline mr-2" />
              Recursos mais alterados
            </h3>
            {stats?.by_resource && stats.by_resource.length > 0 ? (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {stats.by_resource.map((r, i) => (
                  <div key={i} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                    <span className="text-sm">{translateResource(r.resource)}</span>
                    <span className="font-medium">{r.total}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">Sem dados</p>
            )}
          </div>

          {/* Usuários mais ativos */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">
              <Users className="w-5 h-5 inline mr-2" />
              Usuários mais ativos
            </h3>
            {stats?.by_user && stats.by_user.length > 0 ? (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {stats.by_user.map((u, i) => (
                  <div key={i} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                    <span className="text-sm">{u.full_name}</span>
                    <span className="font-medium">{u.total} ações</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">Sem dados</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditLogs;